import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const LifePathNumberCalculator = () => {
  const [birthDate, setBirthDate] = useState('');
  const [lifePathNumber, setLifePathNumber] = useState(null);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);
  const [reducedYear, setReducedYear] = useState(null);
  const [reducedMonth, setReducedMonth] = useState(null);
  const [reducedDay, setReducedDay] = useState(null);

  const handleInputChange = (e) => {
    setBirthDate(e.target.value);
    setError('');
  };

  const calculateLifePathNumber = () => {
    if (!birthDate.trim()) {
      setError('Please enter your birth date');
      return;
    }

    // Split date into components
    const [year, month, day] = birthDate.split('-');

    // Reduce each component to single digit
    const reduceNumber = (num) => {
      while (num > 9 && ![11, 22, 33].includes(num)) {
        num = num.toString().split('').reduce((sum, digit) => sum + +digit, 0);
      }
      return num;
    };

    const reducedYear = reduceNumber(+year);
    const reducedMonth = reduceNumber(+month);
    const reducedDay = reduceNumber(+day);

    setReducedYear(reducedYear);
    setReducedMonth(reducedMonth);
    setReducedDay(reducedDay);

    // Calculate final life path number
    const sum = reducedYear + reducedMonth + reducedDay;
    const finalNumber = reduceNumber(sum);

    setLifePathNumber(finalNumber);

    // Generate chart data
    setChartData({
      labels: ['Year', 'Month', 'Day', 'Total'],
      datasets: [{
        label: 'Reduction Steps',
        data: [reducedYear, reducedMonth, reducedDay, finalNumber],
        backgroundColor: [
          'rgba(255, 99, 132, 0.5)',
          'rgba(54, 162, 235, 0.5)',
          'rgba(75, 192, 192, 0.5)',
          'rgba(153, 102, 255, 0.5)'
        ],
        borderColor: [
          'rgba(255, 99, 132, 1)',
          'rgba(54, 162, 235, 1)',
          'rgba(75, 192, 192, 1)',
          'rgba(153, 102, 255, 1)'
        ],
        borderWidth: 1
      }]
    });
  };

  return (
    <div className="math-calculators max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <Helmet>
        <title>Life Path Number Calculator | Discover Your Life Path</title>
        <meta name="description" content="Calculate your Life Path Number using our Numerology Calculator. Discover your life's purpose and personality traits." />
        <meta name="keywords" content="life path number, numerology calculator, birth date numerology, life purpose, personality traits" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://yourwebsite.com/calculator/life-path-number" />
        <meta property="og:title" content="Life Path Number Calculator | Discover Your Life Path" />
        <meta property="og:description" content="Calculate your Life Path Number using our Numerology Calculator. Discover your life's purpose and personality traits." />
        <meta property="og:image" content="https://yourwebsite.com/images/life-path-og-image.jpg" />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://yourwebsite.com/calculator/life-path-number" />
        <meta property="twitter:title" content="Life Path Number Calculator | Discover Your Life Path" />
        <meta property="twitter:description" content="Calculate your Life Path Number using our Numerology Calculator. Discover your life's purpose and personality traits." />
        <meta property="twitter:image" content="https://yourwebsite.com/images/life-path-og-image.jpg" />
      </Helmet>

      <div className="calculator-header mb-12">
        <div className="flex items-center gap-4">
          <div className="header-icon text-4xl">🔮</div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Life Path Number Calculator</h1>
            <p className="text-lg text-purple-600 font-semibold">Discover Your Life's Purpose</p>
          </div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="calculator-form max-w-2xl mx-auto bg-white rounded-xl shadow-lg p-8"
      >
        <div className="input-group mb-6">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="birthDate">
            Birth Date:
          </label>
          <input
            type="date"
            id="birthDate"
            name="birthDate"
            value={birthDate}
            onChange={handleInputChange}
            className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
          />
        </div>

        <button
          onClick={calculateLifePathNumber}
          className="w-full bg-purple-500 hover:bg-purple-600 text-white font-bold py-3 px-4 rounded focus:outline-none focus:bg-white"
        >
          Calculate Life Path Number
        </button>

        {error && (
          <div className="text-red-500 text-center mt-4">
            {error}
          </div>
        )}

        {lifePathNumber && (
          <div className="results-section mt-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Your Life Path Number</h3>
            <div className="text-center">
              <div className="text-6xl font-bold text-purple-600 mb-4">
                {lifePathNumber}
              </div>
              <p className="text-gray-700">
                This number reveals your life's purpose and core personality traits.
              </p>
            </div>
            {chartData && (
              <div className="space-y-8">
                <div>
                  <h4 className="text-xl font-semibold mb-4">Calculation Breakdown</h4>
                  <div className="h-96">
                    <Bar
                      data={chartData}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                          title: {
                            display: true,
                            text: 'Life Path Number Calculation Steps'
                          }
                        }
                      }}
                    />
                  </div>
                </div>

                <div className="bg-gray-50 p-6 rounded-xl">
                  <h4 className="text-xl font-semibold mb-4">Step-by-Step Explanation</h4>
                  <div className="space-y-4">
                    <div className="p-4 bg-white rounded-lg shadow-inner">
                      <p className="font-medium">1. Year Reduction</p>
                      <p>{birthDate.split('-')[0]} → {reducedYear}</p>
                    </div>
                    <div className="p-4 bg-white rounded-lg shadow-inner">
                      <p className="font-medium">2. Month Reduction</p>
                      <p>{birthDate.split('-')[1]} → {reducedMonth}</p>
                    </div>
                    <div className="p-4 bg-white rounded-lg shadow-inner">
                      <p className="font-medium">3. Day Reduction</p>
                      <p>{birthDate.split('-')[2]} → {reducedDay}</p>
                    </div>
                    <div className="p-4 bg-white rounded-lg shadow-inner">
                      <p className="font-medium">4. Final Calculation</p>
                      <p>{reducedYear} + {reducedMonth} + {reducedDay} = {lifePathNumber}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </motion.div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          How to Use <span className="ml-2 text-2xl">📋</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <p className="text-gray-700 mb-6">
            Follow these steps to calculate your Life Path Number:
          </p>
          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Step 1: Enter Birth Date</h3>
              <p className="text-gray-700">
                Input your complete birth date in the format YYYY-MM-DD.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Step 2: Calculate</h3>
              <p className="text-gray-700">
                Click the "Calculate" button to get your Life Path Number.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Step 3: Understand Results</h3>
              <p className="text-gray-700">
                Review your Life Path Number and its meaning in the results section.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          FAQ <span className="ml-2 text-2xl">❓</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="space-y-8">
            <div>
              <h3 className="text-lg font-semibold mb-2">What is a Life Path Number?</h3>
              <p className="text-gray-700">
                Your Life Path Number is the most important number in numerology, revealing your life's purpose and core personality traits.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-2">How is it calculated?</h3>
              <p className="text-gray-700">
                It's calculated by reducing your birth date (year, month, day) to single digits and summing them, except for master numbers (11, 22, 33).
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-2">What are master numbers?</h3>
              <p className="text-gray-700">
                Master numbers (11, 22, 33) are special numbers in numerology that carry higher spiritual vibrations and aren't reduced further.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Features & Benefits <span className="ml-2 text-2xl">✨</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Key Features</h3>
              <ul className="list-disc pl-8 space-y-3">
                <li>Accurate Life Path Number calculation</li>
                <li>Visual breakdown of calculation steps</li>
                <li>Supports master numbers (11, 22, 33)</li>
                <li>Clean and intuitive interface</li>
                <li>Detailed explanation of results</li>
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Benefits</h3>
              <ul className="list-disc pl-8 space-y-3">
                <li>Discover your life's purpose</li>
                <li>Understand your core personality traits</li>
                <li>Gain insights into your strengths and challenges</li>
                <li>Explore your spiritual path</li>
                <li>Make informed life decisions</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LifePathNumberCalculator;
